import { useEffect, useCallback, useSyncExternalStore } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface UserPresence {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  currentPage: string;
  currentAction: string;
  lastActivity: string;
  online_at: string;
}

interface PresenceState {
  [key: string]: UserPresence[];
}

const getPageName = (pathname: string): string => {
  const routes: Record<string, string> = {
    '/': 'Dashboard',
    '/dashboard': 'Dashboard',
    '/projects': 'Proyek',
    '/schedule': 'Jadwal',
    '/documents': 'Dokumen',
    '/reports': 'Laporan',
    '/budget': 'Budget RAB',
    '/settings': 'Pengaturan',
    '/teams': 'Tim',
    '/admin': 'Admin Dashboard',
    '/admin/activity': 'User Activity',
    '/admin/chat': 'Chat Management',
    '/admin/members': 'Member Management',
    '/admin/maintenance': 'Maintenance',
    '/info': 'Info',
  };
  
  if (routes[pathname]) return routes[pathname];
  
  for (const [path, name] of Object.entries(routes)) {
    if (pathname.startsWith(path) && path !== '/') {
      return name;
    }
  }
  
  return 'Halaman Lain';
};

// ============================================================
// Singleton presence manager – ONE persistent channel
// The channel NEVER tears down while the app is alive.
// It only disconnects when explicitly called via disconnectPresence().
// ============================================================

let sharedChannel: RealtimeChannel | null = null;
let currentOnlineUsers: UserPresence[] = [];
let isChannelConnected = false;
let currentActivity = 'Melihat halaman';
let listeners = new Set<() => void>();
let currentTeamMemberId: string | null = null;

// Keep-alive: re-track every 30s to prevent Supabase from timing out presence
let keepAliveInterval: ReturnType<typeof setInterval> | null = null;
let lastPayload: TrackPayload | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

let usersListeners = new Set<() => void>();
let connListeners = new Set<() => void>();

const emitUsersChange = () => {
  usersListeners.forEach((l) => l());
  listeners.forEach((l) => l());
};
const emitConnChange = () => {
  connListeners.forEach((l) => l());
  listeners.forEach((l) => l());
};

const getSnapshot = () => currentOnlineUsers;
const getConnectedSnapshot = () => isChannelConnected;

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

// Debounce presence sync to avoid rapid-fire re-renders
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let pendingChannel: RealtimeChannel | null = null;

function scheduleSyncPresenceState(channel: RealtimeChannel) {
  pendingChannel = channel;
  if (syncTimer) return; // already scheduled
  syncTimer = setTimeout(() => {
    syncTimer = null;
    if (pendingChannel) doSyncPresenceState(pendingChannel);
  }, 200);
}

function doSyncPresenceState(channel: RealtimeChannel) {
  const state = channel.presenceState() as PresenceState;
  const usersMap = new Map<string, UserPresence>();

  Object.values(state).forEach((presences) => {
    presences.forEach((presence) => {
      const existing = usersMap.get(presence.id);
      if (!existing || new Date(presence.lastActivity) > new Date(existing.lastActivity)) {
        usersMap.set(presence.id, presence);
      }
    });
  });

  const newUsers = Array.from(usersMap.values());
  
  // Only emit if user list actually changed (by id set or count)
  const oldIds = new Set(currentOnlineUsers.map(u => u.id));
  const newIds = new Set(newUsers.map(u => u.id));
  let changed = oldIds.size !== newIds.size;
  if (!changed) {
    for (const id of newIds) {
      if (!oldIds.has(id)) { changed = true; break; }
    }
  }
  // Also check page/action changes for visible updates
  if (!changed) {
    for (const nu of newUsers) {
      const ou = currentOnlineUsers.find(u => u.id === nu.id);
      if (ou && (ou.currentPage !== nu.currentPage || ou.currentAction !== nu.currentAction)) {
        changed = true;
        break;
      }
    }
  }

  if (changed) {
    currentOnlineUsers = newUsers;
    emitUsersChange();
  }
}

interface TrackPayload {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  currentPage: string;
  currentAction: string;
  lastActivity: string;
  online_at: string;
}

/**
 * Connect the presence channel. Safe to call multiple times – 
 * will only create one channel.
 */
function connectPresence(teamMemberId: string, initialPayload: TrackPayload) {
  lastPayload = initialPayload;
  currentTeamMemberId = teamMemberId;
  clearReconnectTimeout();

  if (sharedChannel) {
    // Already connected – just update presence data
    sharedChannel.track(initialPayload).catch(() => {});
    return;
  }

  const channel = supabase.channel('online-users', {
    config: { presence: { key: teamMemberId } },
  });

  channel
    .on('presence', { event: 'sync' }, () => {
      scheduleSyncPresenceState(channel);
    })
    .on('presence', { event: 'join' }, () => {
      scheduleSyncPresenceState(channel);
    })
    .on('presence', { event: 'leave' }, () => {
      scheduleSyncPresenceState(channel);
    })
    .subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        isChannelConnected = true;
        emitConnChange();
        await channel.track(initialPayload);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        isChannelConnected = false;
        emitConnChange();

        if (sharedChannel === channel) {
          sharedChannel = null;
        }

        void supabase.removeChannel(channel).catch(() => {});

        // Auto-reconnect after error
        clearReconnectTimeout();
        reconnectTimeout = setTimeout(() => {
          reconnectTimeout = null;
          if (currentTeamMemberId && lastPayload) {
            connectPresence(currentTeamMemberId, lastPayload);
          }
        }, 3000);
      }
    });

  sharedChannel = channel;

  // Start keep-alive interval
  if (keepAliveInterval) clearInterval(keepAliveInterval);
  keepAliveInterval = setInterval(() => {
    if (sharedChannel && lastPayload) {
      sharedChannel.track({
        ...lastPayload,
        lastActivity: new Date().toISOString(),
      }).catch(() => {});
    }
  }, 60000); // every 60 seconds to reduce overhead with many users
}

/**
 * Disconnect presence channel completely.
 * Only call this on sign-out.
 */
export function disconnectPresence() {
  clearReconnectTimeout();
  if (keepAliveInterval) {
    clearInterval(keepAliveInterval);
    keepAliveInterval = null;
  }
  if (sharedChannel) {
    sharedChannel.unsubscribe();
    void supabase.removeChannel(sharedChannel).catch(() => {});
    sharedChannel = null;
  }
  isChannelConnected = false;
  currentOnlineUsers = [];
  currentTeamMemberId = null;
  lastPayload = null;
  emitUsersChange();
  emitConnChange();
}

async function trackPresence(payload: TrackPayload) {
  lastPayload = payload;
  if (!sharedChannel) return;
  await sharedChannel.track(payload);
}

export const setUserActivity = (action: string) => {
  currentActivity = action;
};

export const setGlobalTrackerActive = (_active: boolean) => {
  // kept for backward compat
};

// ============================================================
// React hook – subscribes to the shared singleton
// ============================================================

export const usePresence = () => {
  const { teamMember } = useAuth();
  const location = useLocation();

  // Subscribe to the external store – use dedicated listener sets
  const onlineUsers = useSyncExternalStore(
    useCallback((cb: () => void) => {
      usersListeners.add(cb);
      return () => { usersListeners.delete(cb); };
    }, []),
    getSnapshot,
  );

  const isConnected = useSyncExternalStore(
    useCallback((cb: () => void) => {
      connListeners.add(cb);
      return () => { connListeners.delete(cb); };
    }, []),
    getConnectedSnapshot,
  );

  // Build payload helper
  const buildPayload = useCallback(
    (action?: string): TrackPayload | null => {
      if (!teamMember?.id) return null;
      return {
        id: teamMember.id,
        name: teamMember.name,
        email: teamMember.email,
        role: teamMember.role,
        avatar: teamMember.imageUrl,
        currentPage: getPageName(location.pathname),
        currentAction: action ?? currentActivity,
        lastActivity: new Date().toISOString(),
        online_at: new Date().toISOString(),
      };
    },
    [teamMember, location.pathname],
  );

  // Connect once when authenticated – NEVER disconnect on component unmount
  useEffect(() => {
    const payload = buildPayload();
    if (!payload || !teamMember?.id) return;

    connectPresence(teamMember.id, payload);
    // NO cleanup – channel is persistent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMember?.id]);

  // Update presence on route change
  useEffect(() => {
    const payload = buildPayload();
    if (!payload) return;
    trackPresence(payload);
  }, [location.pathname, buildPayload]);

  const updateActivity = useCallback(
    async (action: string) => {
      currentActivity = action;
      const payload = buildPayload(action);
      if (payload) await trackPresence(payload);
    },
    [buildPayload],
  );

  return {
    onlineUsers,
    isConnected,
    updateActivity,
    currentUserCount: onlineUsers.length,
  };
};

export const useActivityTracker = () => {
  const { updateActivity } = usePresence();

  return {
    trackEdit: (itemName: string) => updateActivity(`Mengedit ${itemName}`),
    trackCreate: (itemName: string) => updateActivity(`Membuat ${itemName}`),
    trackView: (itemName: string) => updateActivity(`Melihat ${itemName}`),
    trackInput: (formName: string) => updateActivity(`Mengisi ${formName}`),
    trackDelete: (itemName: string) => updateActivity(`Menghapus ${itemName}`),
    resetActivity: () => updateActivity('Melihat halaman'),
  };
};
